import { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import {
  listAdminSettingChanges,
  listAdminSettings,
  type AdminSetting,
} from '@api/admin-settings';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';
import ChangeLog from '../../components/ChangeLog';
import SettingEditor from '../../components/SettingEditor';
import { NOT_AVAILABLE, errorText } from '../../utils/settings';

const SETTINGS_KEY = 'admin-settings';
const CHANGES_KEY = 'admin-settings-changes';

/**
 * Admin settings, super admins only (ZLAYT-1682). Choices such as what an
 * empty demand letter section shows, or the lowest pain score a letter
 * mentions, are changed here instead of in code. Each setting starts at
 * today's behaviour; every change is logged. The server refuses everyone
 * but super admins as well.
 */
function AdminSettings() {
  const isSuperuser = useAuthSelector(
    (state) =>
      (state.userData as { is_superuser?: boolean } | null)?.is_superuser ===
      true
  );
  const [page, setPage] = useState(1);

  const {
    data: settings,
    error,
    mutate,
  } = useSWR<AdminSetting[]>(
    isSuperuser ? SETTINGS_KEY : null,
    listAdminSettings,
    { revalidateOnFocus: false }
  );
  const {
    data: changes,
    error: changesError,
    mutate: mutateChanges,
  } = useSWR(
    isSuperuser ? [CHANGES_KEY, page] : null,
    () => listAdminSettingChanges(page),
    { revalidateOnFocus: false }
  );

  const groups = useMemo(() => {
    const byGroup = new Map<string, AdminSetting[]>();
    (settings ?? []).forEach((setting) => {
      byGroup.set(setting.group, [
        ...(byGroup.get(setting.group) ?? []),
        setting,
      ]);
    });
    return [...byGroup.entries()];
  }, [settings]);

  const saved = (next: AdminSetting) => {
    mutate(
      (prev) =>
        prev?.map((setting) => (setting.key === next.key ? next : setting)),
      { revalidate: false }
    );
    // Page 1 shows the new row; from another page, moving there loads it.
    if (page === 1) mutateChanges();
    else setPage(1);
  };

  if (!isSuperuser) {
    return <Typography sx={{ color: '#475467' }}>{NOT_AVAILABLE}</Typography>;
  }
  if (error) return <Alert severity="error">{errorText(error)}</Alert>;
  if (!settings) return <CircularProgress size={20} />;

  return (
    <Stack spacing={3} sx={{ py: 2, maxWidth: 960 }}>
      <Typography variant="body2" sx={{ color: '#475467' }}>
        Each setting starts at how the app works today. A change applies to
        letters started after it is saved, and is listed in the change log
        below.
      </Typography>
      {groups.map(([group, items]) => (
        <Stack key={group} spacing={1.5}>
          <Typography variant="h6">{group}</Typography>
          {items.map((setting) => (
            <SettingEditor
              key={setting.key}
              setting={setting}
              onSaved={saved}
            />
          ))}
        </Stack>
      ))}
      <Stack spacing={1.5}>
        <Typography variant="h6">Change log</Typography>
        {changesError ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => mutateChanges()}
              >
                Try again
              </Button>
            }
          >
            The change log could not be loaded.
          </Alert>
        ) : (
          <ChangeLog changes={changes} page={page} onPage={setPage} />
        )}
      </Stack>
    </Stack>
  );
}

export default AdminSettings;
